import { useCallback, useEffect, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { useToast } from '../ui/toast.jsx';
import { ConfirmDialog } from '../ui/confirm-dialog.jsx';
import { EmptyState } from '../ui/empty-state.jsx';
import { TypeBadge, SourceBadge, PriorityBadge, ScopeBadge, HomeOnlyBadge, DataCategoryBadge } from '../channels/channel-badges.jsx';
import { ChannelFormModal } from '../channels/channel-form-modal.jsx';
import { MembersModal } from '../channels/members-modal.jsx';
import { api, ApiError } from '../../api/index.js';
import { formatUtcInTimezone } from '../../lib/time.js';

const SOURCE_TABS = [
  { id: 'all',    label: 'All',    icon: 'fa-list' },
  { id: 'oss',    label: 'OSS',    icon: 'fa-sitemap' },
  { id: 'custom', label: 'Custom', icon: 'fa-shapes' },
];

const TYPE_TABS = [
  { id: 'all',       label: 'All',       icon: 'fa-asterisk' },
  { id: 'chat',      label: 'Chat',      icon: 'fa-comments' },
  { id: 'order',     label: 'Order',     icon: 'fa-clipboard-list' },
  { id: 'broadcast', label: 'Broadcast', icon: 'fa-bullhorn' },
];

export function ChannelsPage({ currentUser }) {
  const toast = useToast();
  const isAdmin = currentUser?.role === 'admin';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [sourceFilter, setSourceFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput), 250);
    return () => clearTimeout(id);
  }, [searchInput]);

  const [formState, setFormState] = useState({ open: false, mode: 'create', initial: null });
  const [membersState, setMembersState] = useState({ open: false, channel: null });
  const [deleteState, setDeleteState] = useState({ open: false, channel: null });

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.channels.list({
        source: sourceFilter === 'all' ? '' : sourceFilter,
        type: typeFilter === 'all' ? '' : typeFilter,
        q: search,
      });
      setData(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message);
    } finally {
      setLoading(false);
    }
  }, [sourceFilter, typeFilter, search]);

  useEffect(() => { refresh(); }, [refresh]);

  async function handleSave(payload) {
    if (formState.mode === 'create') {
      await api.channels.createCustom(payload);
      toast.success(`Channel "${payload.name}" created.`);
    } else {
      await api.channels.update(formState.initial.id, payload);
      toast.success('Channel updated.');
    }
    setFormState({ open: false, mode: 'create', initial: null });
    await refresh();
  }

  async function handleDelete() {
    try {
      await api.channels.remove(deleteState.channel.id);
      toast.success(`Channel "${deleteState.channel.name}" deleted.`);
      setDeleteState({ open: false, channel: null });
      await refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Delete failed.');
    }
  }

  const channels = data?.channels || [];
  const stats = data?.stats || { total: 0, oss: 0, custom: 0, archived: 0 };

  return (
    <PageShell
      icon="fa-comments"
      title="Channels"
      subtitle="Chat / Order / Broadcast — built on top of OSS, plus ad-hoc custom channels"
      actions={
        isAdmin && (
          <button
            type="button"
            onClick={() => setFormState({ open: true, mode: 'create', initial: null })}
            className="btn-primary"
          >
            <i className="fa-solid fa-plus" aria-hidden="true" />
            Create custom channel
          </button>
        )
      }
    >
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard icon="fa-comments"    label="Total"    value={stats.total}    tone="accent" />
        <StatCard icon="fa-sitemap"     label="OSS"      value={stats.oss}      tone="success" />
        <StatCard icon="fa-shapes"      label="Custom"   value={stats.custom}   tone="mist" />
        <StatCard icon="fa-box-archive" label="Archived" value={stats.archived} tone="warning" />
      </div>

      {/* Filters */}
      <div className="card mb-4 flex flex-col gap-3 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <FilterTabs items={SOURCE_TABS} value={sourceFilter} onChange={setSourceFilter} />
          <FilterTabs items={TYPE_TABS}   value={typeFilter}   onChange={setTypeFilter} />
        </div>
        <div className="relative w-full lg:max-w-xs">
          <i className="fa-solid fa-magnifying-glass pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-mist" aria-hidden="true" />
          <input
            type="search"
            className="field pl-10"
            placeholder="Name, path, description…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div role="alert" className="card mb-4 border-rose-200 bg-rose-soft px-5 py-4 text-sm text-rose-700">
          <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="card px-6 py-16 text-center text-mist">
          <span className="loader-spinner mx-auto mb-3 text-accent" aria-hidden="true" />
          <div className="text-sm">Loading channels…</div>
        </div>
      ) : channels.length === 0 ? (
        <EmptyState
          icon="fa-comments"
          title="No channels match the filters"
          description={
            sourceFilter !== 'all' || typeFilter !== 'all' || search
              ? 'Try clearing filters or the search field.'
              : 'OSS channels are created from the OSS page (toggle on a unit). Custom channels are created here.'
          }
        />
      ) : (
        <ChannelTable
          rows={channels}
          viewerTimezone={currentUser?.timezone || 'UTC'}
          isAdmin={isAdmin}
          onMembers={(c) => setMembersState({ open: true, channel: c })}
          onEdit={(c) => setFormState({ open: true, mode: 'edit', initial: c })}
          onDelete={(c) => setDeleteState({ open: true, channel: c })}
        />
      )}

      <ChannelFormModal
        open={formState.open}
        mode={formState.mode}
        initial={formState.initial}
        onClose={() => setFormState({ open: false, mode: 'create', initial: null })}
        onSave={handleSave}
      />

      <MembersModal
        open={membersState.open}
        channel={membersState.channel}
        onClose={() => setMembersState({ open: false, channel: null })}
        onChanged={refresh}
      />

      <ConfirmDialog
        open={deleteState.open}
        title={`Delete "${deleteState.channel?.name || ''}"?`}
        message={
          deleteState.channel?.source === 'oss'
            ? 'This removes the OSS-derived channel for this unit. The unit and its personnel are unaffected. The channel can be recreated later.'
            : 'This permanently removes the custom channel and its membership.'
        }
        confirmLabel="Delete"
        destructive
        onClose={() => setDeleteState({ open: false, channel: null })}
        onConfirm={handleDelete}
      />
    </PageShell>
  );
}

function FilterTabs({ items, value, onChange }) {
  return (
    <div className="flex items-center gap-1 rounded-2xl border border-line bg-white p-1">
      {items.map((t) => {
        const active = value === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={
              active
                ? 'flex items-center gap-1.5 rounded-xl bg-accent-soft px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-accent'
                : 'flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-mist hover:text-ink'
            }
          >
            <i className={`fa-solid ${t.icon} text-[10px]`} aria-hidden="true" />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function ChannelTable({ rows, viewerTimezone, isAdmin, onMembers, onEdit, onDelete }) {
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-line bg-canvas/60 text-left text-[11px] font-semibold uppercase tracking-wider text-mist">
              <th className="px-5 py-3">Channel</th>
              <th className="px-5 py-3">Type</th>
              <th className="px-5 py-3">Data</th>
              <th className="px-5 py-3">Source</th>
              <th className="px-5 py-3">Members</th>
              <th className="px-5 py-3">Priority</th>
              <th className="px-5 py-3">Updated</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-b border-line last:border-b-0 hover:bg-canvas/40">
                <td className="px-5 py-3 align-middle">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
                      <i className="fa-solid fa-comments" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-semibold text-ink">{c.name}</span>
                        <HomeOnlyBadge active={c.homeNodeOnly} />
                        {c.archived && (
                          <span className="rounded-full border border-line bg-canvas px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-mist">
                            archived
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 truncate font-mono text-[11px] text-mist">{c.path}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 align-middle"><TypeBadge type={c.type} /></td>
                <td className="px-5 py-3 align-middle"><DataCategoryBadge dataTypes={c.dataTypes} /></td>
                <td className="px-5 py-3 align-middle">
                  <div className="flex flex-col gap-1">
                    <SourceBadge source={c.source} />
                    {c.source === 'oss' && <ScopeBadge scope={c.scope} />}
                  </div>
                </td>
                <td className="px-5 py-3 align-middle">
                  <span className="font-semibold text-ink">{c.memberCount}</span>
                </td>
                <td className="px-5 py-3 align-middle"><PriorityBadge priority={c.priority} /></td>
                <td className="px-5 py-3 align-middle text-xs text-mist">
                  {formatUtcInTimezone(c.updatedAt, viewerTimezone)}
                </td>
                <td className="px-5 py-3 align-middle">
                  <div className="flex justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => onMembers(c)}
                      title="Members"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-canvas hover:text-ink"
                    >
                      <i className="fa-solid fa-users" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onEdit(c)}
                      disabled={!isAdmin}
                      title="Edit settings"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-canvas hover:text-ink disabled:opacity-30"
                    >
                      <i className="fa-solid fa-pen" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(c)}
                      disabled={!isAdmin}
                      title="Delete"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-rose-soft hover:text-rose-600 disabled:opacity-30"
                    >
                      <i className="fa-solid fa-trash" aria-hidden="true" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, tone = 'accent' }) {
  const tones = {
    accent:  'bg-accent-soft text-accent',
    success: 'bg-success-soft text-success',
    warning: 'bg-warning-soft text-warning',
    mist:    'bg-canvas text-mist',
  };
  return (
    <div className="card flex items-center justify-between px-5 py-4">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-mist">{label}</div>
        <div className="mt-0.5 text-2xl font-semibold tracking-tight text-ink">{value}</div>
      </div>
      <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tones[tone]}`}>
        <i className={`fa-solid ${icon} text-lg`} aria-hidden="true" />
      </div>
    </div>
  );
}
